import { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Flex,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import axiosInstance from "../axios";

const { Option } = Select;

function flattenStateMaster(statesResponse) {
  if (!Array.isArray(statesResponse)) return [];
  return statesResponse.flatMap((item) =>
    Object.entries(item).map(([id, name]) => ({
      id: Number(id),
      name,
    })),
  );
}

const CityMaster = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [states, setStates] = useState([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState(undefined);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    fetchData(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, stateFilter]);

  const fetchStates = async () => {
    try {
      const response = await axiosInstance.get("/v1/cre/master/state/all");
      setStates(flattenStateMaster(response.data));
    } catch (error) {
      message.error("Failed to load states");
    }
  };

  const fetchData = async (page, limit) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/v1/cre/master/city", {
        params: {
          page,
          limit,
          search: search || undefined,
          state_id: stateFilter || undefined,
        },
      });
      if (response.data?.is_success) {
        setData(response.data.data || []);
        setPagination({
          current: page,
          pageSize: limit,
          total: response.data.meta?.total || 0,
        });
      }
    } catch (error) {
      message.error("Failed to fetch cities");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      name: record.name,
      state_id: record.state_id || undefined,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axiosInstance.delete(`/v1/cre/master/city/${id}`);
      if (response.data?.is_success) {
        message.success("City deleted successfully");
        fetchData(pagination.current, pagination.pageSize);
      } else {
        message.error(response.data?.error?.message || "Failed to delete city");
      }
    } catch (error) {
      message.error(error.response?.data?.error?.message || "Failed to delete city");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      if (editingItem) {
        const response = await axiosInstance.put(`/v1/cre/master/city/${editingItem.id}`, values);
        if (response.data?.is_success) {
          message.success("City updated successfully");
        } else {
          message.error(response.data?.error?.message || "Failed to update city");
          return;
        }
      } else {
        const response = await axiosInstance.post("/v1/cre/master/city", values);
        if (response.data?.is_success) {
          message.success("City created successfully");
        } else {
          message.error(response.data?.error?.message || "Failed to create city");
          return;
        }
      }
      setModalOpen(false);
      fetchData(pagination.current, pagination.pageSize);
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.response?.data?.error?.message || "Failed to save city");
    } finally {
      setSubmitLoading(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "City Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "State",
      dataIndex: "state_name",
      key: "state_name",
      render: (stateName) => stateName || "—",
    },
    {
      title: "Actions",
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Popconfirm
            title="Delete this city?"
            description="This cannot be undone if the city is not in use."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (nextPagination) => {
    fetchData(nextPagination.current, nextPagination.pageSize);
  };

  return (
    <Flex vertical style={{ padding: "8px", width: "100%" }} gap={12}>
      <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
        <Space wrap>
          <Input
            placeholder="Search city name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            style={{ width: 220 }}
          />
          <Select
            placeholder="Filter by state"
            value={stateFilter}
            onChange={setStateFilter}
            allowClear
            style={{ width: 220 }}
          >
            {states.map((state) => (
              <Option key={state.id} value={state.id}>
                {state.name}
              </Option>
            ))}
          </Select>
          <Button icon={<ReloadOutlined />} onClick={() => fetchData(pagination.current, pagination.pageSize)}>
            Refresh
          </Button>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add City
        </Button>
      </Flex>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
        }}
        onChange={handleTableChange}
      />

      <Modal
        title={editingItem ? "Edit City" : "Add City"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => !submitLoading && setModalOpen(false)}
        okText={editingItem ? "Update" : "Create"}
        confirmLoading={submitLoading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="City Name"
            name="name"
            rules={[{ required: true, message: "Please enter city name" }]}
          >
            <Input placeholder="Enter city name" />
          </Form.Item>
          <Form.Item label="State" name="state_id">
            <Select placeholder="Select state" allowClear showSearch optionFilterProp="children">
              {states.map((state) => (
                <Option key={state.id} value={state.id}>
                  {state.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Flex>
  );
};

export default CityMaster;
